import { shareKeySignal } from "@src/auth/auth-utils";
import { useArchiveWhiteboard } from "@src/components/board/mutations/archive-whiteboard";
import { useCloneWhiteboard } from "@src/components/board/mutations/clone-whiteboard";
import { useCreateFolder } from "@src/components/board/mutations/create-folder";
import { useCreateWhiteboard } from "@src/components/board/mutations/create-whiteboard";
import { useDeleteFolder } from "@src/components/board/mutations/delete-folder";
import { useMoveRanklistItem } from "@src/components/board/mutations/move-item";
import { useRenameFolder } from "@src/components/board/mutations/rename-folder";
import { useRenameWhiteboard } from "@src/components/board/mutations/rename-whiteboard";
import { generateNewName } from "@src/components/board/mutations/utils";
import { fetchActiveWhiteboardsQueryOptions } from "@src/components/board/queries/fetch-active-whiteboards";
import {
	type NavbarBranchItem,
	fetchNavbarTreeQueryOptions,
	findNavbarBranchItemInCache,
	navbarBranchIsSharedOrPublished,
	navbarBranchQueryKeys,
	seedBranchCacheFromTree,
} from "@src/components/board/queries/fetch-ranklist";
import { generateWhiteboardName } from "@src/components/board/utils";
import { WhiteboardDocumentId } from "@src/components/collaboration/document-id";
import { useAuthenticatedUser } from "@src/components/user/user-context";
import { NavBarItemType, NavbarSection } from "@src/gql/graphql";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import type {
	LibraryAgentActions,
	LibraryAgentTreeItem,
} from "./library-agent-context";

const DEFAULT_LIBRARY_SECTION = NavbarSection.MyWhiteboards;

function toLibraryTreeItem(item: NavbarBranchItem): LibraryAgentTreeItem {
	return {
		key: item.key,
		name: item.name,
		type: item.type,
		parentKey: item.parentKey ?? null,
		hasChildren: item.hasChildren,
		isShared: item.collaboration?.isShared ?? false,
		isPublished: item.publication?.isPublished ?? false,
	};
}

function resolveLibrarySection(section?: NavbarSection) {
	return section ?? DEFAULT_LIBRARY_SECTION;
}

function formatLibraryError(error: unknown, fallback: string) {
	if (error instanceof Error && error.message.trim().length > 0) {
		return error.message;
	}

	return fallback;
}

export function useLibraryAgentActions(): LibraryAgentActions {
	const queryClient = useQueryClient();
	const authenticatedUser = useAuthenticatedUser();
	const { mutateAsync: createWhiteboard } = useCreateWhiteboard();
	const { mutateAsync: renameWhiteboard } = useRenameWhiteboard();
	const { mutateAsync: createFolder } = useCreateFolder();
	const { mutateAsync: renameFolder } = useRenameFolder();
	const { mutateAsync: deleteFolder } = useDeleteFolder();
	const { mutateAsync: moveRanklistItem } = useMoveRanklistItem();
	const { mutateAsync: cloneWhiteboard } = useCloneWhiteboard(false, false);
	const { mutateAsync: moveWhiteboardToTrash } = useArchiveWhiteboard(false);

	const listNavbarTree = useCallback<LibraryAgentActions["listNavbarTree"]>(
		async (input = {}) => {
			const section = resolveLibrarySection(input.section);
			const items = await queryClient.fetchQuery(
				fetchNavbarTreeQueryOptions(section),
			);
			seedBranchCacheFromTree(queryClient, section, items);

			return {
				section,
				itemCount: items.length,
				items: items.map(toLibraryTreeItem),
			};
		},
		[queryClient],
	);

	const createWhiteboardAction = useCallback<
		LibraryAgentActions["createWhiteboard"]
	>(
		async ({ name, parentFolderKey = null }) => {
			const activeWhiteboards = await queryClient.fetchQuery(
				fetchActiveWhiteboardsQueryOptions(
					authenticatedUser.id,
					shareKeySignal.value,
				),
			);
			const resolvedName =
				name.trim().length > 0
					? name.trim()
					: generateWhiteboardName((activeWhiteboards?.length ?? 0) + 1);

			const result = await createWhiteboard({
				id: new WhiteboardDocumentId(),
				name: resolvedName,
			});

			const whiteboardId = result.id;
			if (!whiteboardId) {
				throw new Error("Failed to create whiteboard");
			}

			if (parentFolderKey) {
				const section = DEFAULT_LIBRARY_SECTION;
				const branchItems =
					queryClient.getQueryData<NavbarBranchItem[]>(
						navbarBranchQueryKeys.branch(section, parentFolderKey),
					) ?? [];
				const firstItemKey = branchItems[0]?.key;

				await moveRanklistItem({
					section: DEFAULT_LIBRARY_SECTION,
					itemKey: whiteboardId,
					parentKey: parentFolderKey,
					sourceParentKey: null,
					beforeKey: firstItemKey ?? null,
				});
			}

			return {
				whiteboardId,
				name: result.data?.whiteboard?.name ?? resolvedName,
				parentKey: parentFolderKey ?? null,
			};
		},
		[authenticatedUser.id, createWhiteboard, moveRanklistItem, queryClient],
	);

	const renameWhiteboardAction = useCallback<
		LibraryAgentActions["renameWhiteboard"]
	>(
		async ({ whiteboardId, name }) => {
			const trimmedName = name.trim();
			if (trimmedName.length === 0) {
				throw new Error("Whiteboard name cannot be empty");
			}

			const renamed = await renameWhiteboard({
				id: whiteboardId,
				name: trimmedName,
			});

			return {
				whiteboardId,
				name: renamed?.name ?? trimmedName,
			};
		},
		[renameWhiteboard],
	);

	const createFolderAction = useCallback<LibraryAgentActions["createFolder"]>(
		async ({ name, parentFolderKey = null }) => {
			const trimmedName = name.trim();
			if (trimmedName.length === 0) {
				throw new Error("Folder name cannot be empty");
			}

			const section = DEFAULT_LIBRARY_SECTION;
			const branchItems =
				queryClient.getQueryData<NavbarBranchItem[]>(
					navbarBranchQueryKeys.branch(section, parentFolderKey),
				) ?? [];
			const firstItemKey = branchItems[0]?.key;

			const createdFolder = await createFolder({
				section,
				name: trimmedName,
				parentFolderKey,
				beforeKey: firstItemKey ?? null,
			});

			if (!createdFolder?.key) {
				throw new Error("Failed to create folder");
			}

			return {
				folderKey: createdFolder.key,
				name: createdFolder.name,
				parentKey: createdFolder.parentKey ?? null,
			};
		},
		[createFolder, queryClient],
	);

	const renameFolderAction = useCallback<LibraryAgentActions["renameFolder"]>(
		async ({ folderKey, name }) => {
			const trimmedName = name.trim();
			if (trimmedName.length === 0) {
				throw new Error("Folder name cannot be empty");
			}

			const renamed = await renameFolder({
				section: DEFAULT_LIBRARY_SECTION,
				folderKey,
				newName: trimmedName,
			});

			if (!renamed?.key) {
				throw new Error("Failed to rename folder");
			}

			return {
				folderKey: renamed.key,
				name: renamed.name,
			};
		},
		[renameFolder],
	);

	const deleteFolderAction = useCallback<LibraryAgentActions["deleteFolder"]>(
		async ({ folderKey }) => {
			const section = DEFAULT_LIBRARY_SECTION;
			const folderItem = findNavbarBranchItemInCache(
				queryClient,
				section,
				folderKey,
			);

			if (folderItem && folderItem.type !== NavBarItemType.Folder) {
				throw new Error("Only folders can be deleted with deleteFolder");
			}

			const children =
				queryClient.getQueryData<NavbarBranchItem[]>(
					navbarBranchQueryKeys.branch(section, folderKey),
				) ?? [];

			if (children.length > 0) {
				throw new Error(
					"Folder is not empty. Move or delete its contents first.",
				);
			}

			const deleted = await deleteFolder({
				section,
				key: folderKey,
			});

			if (!deleted) {
				throw new Error("Failed to delete folder");
			}

			return {
				folderKey,
				deleted: true,
			};
		},
		[deleteFolder, queryClient],
	);

	const moveNavbarItemAction = useCallback<
		LibraryAgentActions["moveNavbarItem"]
	>(
		async ({
			itemKey,
			parentKey = null,
			sourceParentKey = null,
			beforeKey = null,
			afterKey = null,
		}) => {
			const movedItems = await moveRanklistItem({
				section: DEFAULT_LIBRARY_SECTION,
				itemKey,
				parentKey,
				sourceParentKey,
				beforeKey,
				afterKey,
			});

			return {
				movedKeys: movedItems.map((item) => item.key),
				parentKey,
			};
		},
		[moveRanklistItem],
	);

	const duplicateWhiteboardAction = useCallback<
		LibraryAgentActions["duplicateWhiteboard"]
	>(
		async ({ whiteboardId, name }) => {
			const section = DEFAULT_LIBRARY_SECTION;
			const branchItems =
				queryClient.getQueryData<NavbarBranchItem[]>(
					navbarBranchQueryKeys.branch(section, null),
				) ?? [];
			const sourceItem = findNavbarBranchItemInCache(
				queryClient,
				section,
				whiteboardId,
			);
			const sourceName = sourceItem?.name ?? "Whiteboard";
			const activeNames = branchItems
				.filter((item) => item.type === NavBarItemType.Board)
				.map((item) => item.name);
			const resolvedName =
				name?.trim() || generateNewName(sourceName, activeNames);

			const result = await cloneWhiteboard({
				id: whiteboardId,
				name: resolvedName,
			});

			if (!result.id) {
				throw new Error("Failed to duplicate whiteboard");
			}

			return {
				whiteboardId: result.id,
				name: result.data?.state?.name ?? resolvedName,
				sourceWhiteboardId: whiteboardId,
			};
		},
		[cloneWhiteboard, queryClient],
	);

	const deleteWhiteboardAction = useCallback<
		LibraryAgentActions["deleteWhiteboard"]
	>(
		async ({ whiteboardId }) => {
			const branchItem = findNavbarBranchItemInCache(
				queryClient,
				DEFAULT_LIBRARY_SECTION,
				whiteboardId,
			);

			if (branchItem && navbarBranchIsSharedOrPublished(branchItem)) {
				return {
					whiteboardId,
					deleted: false as const,
					error:
						"Cannot delete a shared or published whiteboard. Remove sharing or unpublish it first.",
				};
			}

			try {
				await moveWhiteboardToTrash(whiteboardId);
				return {
					whiteboardId,
					deleted: true as const,
				};
			} catch (error) {
				return {
					whiteboardId,
					deleted: false as const,
					error: formatLibraryError(error, "Failed to delete whiteboard"),
				};
			}
		},
		[moveWhiteboardToTrash, queryClient],
	);

	return useMemo(
		() => ({
			listNavbarTree,
			createWhiteboard: createWhiteboardAction,
			renameWhiteboard: renameWhiteboardAction,
			createFolder: createFolderAction,
			renameFolder: renameFolderAction,
			deleteFolder: deleteFolderAction,
			moveNavbarItem: moveNavbarItemAction,
			duplicateWhiteboard: duplicateWhiteboardAction,
			deleteWhiteboard: deleteWhiteboardAction,
		}),
		[
			deleteWhiteboardAction,
			createFolderAction,
			createWhiteboardAction,
			deleteFolderAction,
			duplicateWhiteboardAction,
			listNavbarTree,
			moveNavbarItemAction,
			renameFolderAction,
			renameWhiteboardAction,
		],
	);
}
